/**
 * One knob, as a labelled row.
 *
 * The label and the line under it are the knob's own words; the thing you drag is whatever the
 * knob's control kind builds. A knob with no hint gets no line, not an empty one. Splitting the
 * row from the screen is what keeps the screen a loop over the registry: a row knows about one
 * knob, and the screen knows about none.
 */

import { controlFor } from '@/ui/settings/settingControlRegistry';
import { el } from '@/ui/dom';
import type { SettingDefinition, SettingValue } from '@/ui/settings/settingContract';

export interface SettingRowOptions {
  readonly definition: SettingDefinition;
  readonly value: SettingValue;
  readonly onChange: (value: SettingValue) => void;
  readonly onCommit: (value: SettingValue) => void;
}

export interface SettingRow {
  readonly root: HTMLElement;
  dispose(): void;
}

export function createSettingRow(options: SettingRowOptions): SettingRow {
  const definition = options.definition;
  const root = el('div', `settings-row settings-row-${definition.id}`);

  const words = el('div', 'settings-words');
  words.append(el('div', 'settings-label', definition.label));
  if (definition.hint !== '') words.append(el('p', 'settings-hint', definition.hint));

  const control = controlFor(definition.control.kind).create({
    definition,
    value: options.value,
    onChange: options.onChange,
    onCommit: options.onCommit,
  });

  root.append(words, control.root);

  return {
    root,
    dispose(): void {
      control.dispose();
      root.remove();
    },
  };
}
